import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const sections = [
  {
    title: 'Information We Collect',
    icon: 'ri-database-2-line',
    content: `We collect information you provide directly to us when using SwiftTrack services. This includes:
    
    • Shipment information: sender and recipient names, addresses, contact numbers, and package details
    • Account information: email address and authentication credentials when creating an admin account
    • Tracking queries: tracking numbers entered into our search system
    • Communication data: messages sent through our contact forms
    • Technical data: IP address, browser type, device identifiers, and usage patterns`,
  },
  {
    title: 'How We Use Your Information',
    icon: 'ri-settings-3-line',
    content: `We use the collected information to:
    
    • Provide, maintain, and improve our shipment tracking services
    • Process and manage shipment records and status updates
    • Send transactional emails including shipment status notifications via Resend
    • Respond to customer inquiries and support requests
    • Monitor and analyze usage patterns to enhance user experience
    • Comply with legal obligations and enforce our terms`,
  },
  {
    title: 'Data Sharing & Disclosure',
    icon: 'ri-share-forward-line',
    content: `We do not sell, trade, or rent your personal information to third parties. We may share information with:
    
    • Service providers: trusted partners who assist in operating our platform (Supabase for database hosting, Resend for email delivery)
    • Carriers and logistics partners: only the information necessary to process and deliver shipments
    • Legal authorities: when required by law, court order, or governmental regulation
    • Business transfers: in the event of a merger, acquisition, or asset sale, your information may be transferred`,
  },
  {
    title: 'Data Security',
    icon: 'ri-shield-check-line',
    content: `We implement robust security measures to protect your personal information:
    
    • All data is encrypted in transit using TLS/SSL protocols
    • Database access is controlled through Row Level Security (RLS) policies
    • Admin access requires secure authentication through Supabase Auth
    • We regularly review and update our security practices
    • We limit access to personal data to authorized personnel only`,
  },
  {
    title: 'Cookies & Tracking Technologies',
    icon: 'ri-cookie-line',
    content: `We use cookies and similar technologies to enhance your experience:
    
    • Essential cookies: required for the website to function properly
    • Performance cookies: help us understand how visitors interact with our site
    • Preference cookies: remember your settings and choices
    
    You can control cookie settings through your browser preferences. Note that disabling certain cookies may affect website functionality. See our Cookie Policy for more details.`,
  },
  {
    title: 'Your Rights',
    icon: 'ri-user-settings-line',
    content: `You have the following rights regarding your personal data:
    
    • Access: request a copy of the personal information we hold about you
    • Correction: request correction of inaccurate or incomplete information
    • Deletion: request deletion of your personal data (subject to legal obligations)
    • Portability: receive your data in a structured, machine-readable format
    • Objection: object to processing of your personal data in certain circumstances
    
    To exercise these rights, please contact us at privacy@swifttrack.com`,
  },
  {
    title: 'Data Retention',
    icon: 'ri-time-line',
    content: `We retain personal information for as long as necessary to fulfill the purposes outlined in this policy:
    
    • Shipment records: retained for 7 years for business and legal compliance purposes
    • Account information: retained until account deletion is requested
    • Contact form submissions: retained for 2 years
    • Usage logs and analytics: retained for 12 months
    
    After the retention period, data is securely deleted or anonymized.`,
  },
  {
    title: 'Contact Us',
    icon: 'ri-mail-line',
    content: `If you have questions about this Privacy Policy or our privacy practices, please contact us:
    
    • Email: privacy@swifttrack.com
    • Address: 123 Logistics Hub, Global Trade Center, Suite 400
    • Phone: +1 (888) 794-8325
    
    We will respond to your inquiry within 30 business days.`,
  },
];

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <div className="pt-28 pb-14" style={{ background: 'linear-gradient(135deg, #0d2b2b 0%, var(--primary) 100%)' }}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{ background: 'rgba(255,255,255,0.1)' }}>
            <i className="ri-shield-user-line text-white text-2xl" />
          </div>
          <h1 className="font-heading font-bold text-4xl text-white mb-4">Privacy Policy</h1>
          <p className="text-white/70 text-base max-w-xl mx-auto leading-relaxed">
            We are committed to protecting your privacy and handling your data with transparency and care.
          </p>
          <p className="text-white/40 text-sm mt-4">Last updated: March 25, 2026</p>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="max-w-4xl mx-auto px-6 pt-8 pb-2">
        <nav className="flex items-center gap-2 text-sm text-gray-400">
          <Link to="/" className="hover:text-gray-600 transition-colors">Home</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-gray-600">Privacy Policy</span>
        </nav>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-10 pb-20">
        <div className="bg-amber-50 border border-amber-200/60 rounded-2xl p-5 mb-10 flex items-start gap-4">
          <i className="ri-information-line text-amber-500 text-xl flex-shrink-0 mt-0.5" />
          <p className="text-amber-800 text-sm leading-relaxed">
            This Privacy Policy describes how SwiftTrack collects, uses, and protects information about you when you use our shipment tracking services. By using our services, you agree to the practices described in this policy.
          </p>
        </div>

        <div className="space-y-8">
          {sections.map((section, index) => (
            <div key={index} className="border border-gray-100 rounded-2xl p-7">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'var(--primary-light)' }}>
                  <i className={`${section.icon} text-base`} style={{ color: 'var(--primary)' }} />
                </div>
                <h2 className="font-heading font-semibold text-lg text-gray-900">{section.title}</h2>
              </div>
              <div className="text-gray-600 text-sm leading-relaxed whitespace-pre-line pl-12">
                {section.content}
              </div>
            </div>
          ))}
        </div>

        {/* Footer nav */}
        <div className="mt-12 pt-8 border-t border-gray-100 flex flex-wrap gap-4 justify-center">
          <Link to="/terms-of-service" className="text-sm font-medium hover:underline cursor-pointer" style={{ color: 'var(--primary)' }}>
            Terms of Service
          </Link>
          <span className="text-gray-300">·</span>
          <Link to="/cookie-policy" className="text-sm font-medium hover:underline cursor-pointer" style={{ color: 'var(--primary)' }}>
            Cookie Policy
          </Link>
          <span className="text-gray-300">·</span>
          <Link to="/contact" className="text-sm font-medium hover:underline cursor-pointer" style={{ color: 'var(--primary)' }}>
            Contact Us
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}
